import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { environment } from '../../environments/environment';
import { MssListFilters } from '../models/mss-list-filters';
import { ManuscriptList, Manuscript } from '../models/manuscript';
import { MsImageFileList } from '../models/ms-image-file';
import { MsImageLinkList, MsImageLinkSelection, MsImageLink } from '../models/ms-image-link';
import { MsWorkList } from '../models/ms-work';
import { MsHistoryCopyList } from '../models/ms-history-copy';
import { MsTextTransmissionList } from '../models/ms-text-transmission';
import { MsBindingList } from '../models/ms-binding';
import { MsDecorationList } from '../models/ms-decoration';
import { MsBiblioLinkList } from '../models/ms-biblio-link';
import { SifaSelection } from '../models/sifa-selection';
import { ListFilters } from '../models/list-filters';
import { ImageGetNamePipe } from '../pipes/image-get-name.pipe';
import { NameList } from '../models/name-list';

/* Http parameters type for manuscript */
export type MssHttpParamsTypes = 'msList' | 'ms' | 'msImageLinks' | 'imageLinks' | 'msWorks' | 'msHistoryCopies'
    | 'msHistoryOwners' | 'msHistoryPatrons' | 'msTextTransmissions' | 'msBindings'
    | 'msDecorations' | 'biblioLinks' | 'msImageFiles';

@Injectable()
export class ManuscriptsService {
    private httpHeaders: HttpHeaders;
    private httpParams: Map<MssHttpParamsTypes, HttpParams>;
    private mssListFilters$: BehaviorSubject<MssListFilters>;
    private manuscripts$: BehaviorSubject<ManuscriptList>;
    private manuscript$: BehaviorSubject<Manuscript>;
    private msImageLinks$: BehaviorSubject<MsImageLinkList>;
    private msWritingSupportImageLinks$: BehaviorSubject<MsImageLinkList>;
    private msTextBlockImageLinks$: BehaviorSubject<MsImageLinkList>;
    private msHandsWritingsImageLinks$: BehaviorSubject<MsImageLinkList>;
    private msInkHeadingsImageLinks$: BehaviorSubject<MsImageLinkList>;
    private msStateOfPreservationImageLinks$: BehaviorSubject<MsImageLinkList>;
    private msWorks$: BehaviorSubject<MsWorkList>;
    private msHistoryCopies$: BehaviorSubject<MsHistoryCopyList>;
    private msHistoryOwners$: BehaviorSubject<NameList>;
    private msHistoryPatrons$: BehaviorSubject<NameList>;
    private msTextTransmissions$: BehaviorSubject<MsTextTransmissionList>;
    private msBindings$: BehaviorSubject<MsBindingList>;
    private msDecorations$: BehaviorSubject<MsDecorationList>;
    private msBiblioLinks$: BehaviorSubject<MsBiblioLinkList>;
    private msImageLinkSelection$: BehaviorSubject<MsImageLinkSelection>;
    private msImageFileList$: BehaviorSubject<MsImageFileList>;

    constructor(
        private http: HttpClient,
        private imageGetNamePipe: ImageGetNamePipe
        ) {
        this.init();
    }

    init(): void {
        /* Common headers */
        this.httpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': environment.apiKey,
            'X-PhiBor-App': environment.phiborApp
        });

        /* Manuscript list http params */
        this.httpParams = new Map<MssHttpParamsTypes, HttpParams>();
        this.httpParams.set('msList', new HttpParams()
            .set('fields', 'pkid,fkidms_types,shelf_mark,shelf_mark_norm,private_collection,suffix,fkidimg_folders,images_linked,fkidlibraries,ed_witness')
            .set('expand', 'fkidcountries,fkidcities,fkidlibraries,__works,__date,__imagesFolder')
            .set('sort', 'fkidcountries,fkidcities,fkidlibraries,shelf_mark_norm')
        );

        /* Manuscript http params */
        this.httpParams.set('ms', new HttpParams()
            .set('expand', 'fkidcountries,fkidcities,fkidlibraries,fkidregions,fkidwriting_hands,fkidinks'
                + ',__languages,__subjects,__works,__children')
        );

        /* Manuscript image links http params */
        this.httpParams.set('msImageLinks', new HttpParams()
            .set('fields', 'image,pkid,fkidimg_files,fkidgridstruct_sections,fkidgridstruct_parts,fkidgridstruct_treatises'
                + ',fkidgridstruct_chapters,fkidms_structure,fkidmanuscripts,version')
            .set('sort', 'image')
        );

        /* Image links http params */
        this.httpParams.set('imageLinks', new HttpParams()
            .set('fields', 'pkid,image,fkidimg_files,fkidimg_annotations,fkidmanuscripts,version')
            .set('expand', '__annotations')
            .set('sort', 'image')
        );

        /* Manuscript works http params */
        this.httpParams.set('msWorks', new HttpParams()
            .set('fields', 'pkid,title_orig,place,title_conv,title_conv_enc,inscription,date,author_full_name'
                + ',author_conv_name,comments,biblio_serial_number,author_dates,sorting,version')
            .set('expand', '__work_copies')
            .set('sort', 'sorting,pkid')
        );

        /* Manuscript history copies http params */
        this.httpParams.set('msHistoryCopies', new HttpParams()
            .set('fields', 'pkid,creation_date,fkidmanuscripts,creation_date_from_ad,creation_date_to_ad'
                + ',creation_date_from_ah,creation_date_to_ah,comments,sorting,version')
            .set('expand', '__images,__annotations,__places,__modes,__copyists')
            .set('sort', 'sorting,pkid')
        );

        /* Manuscript history owners http params */
        this.httpParams.set('msHistoryOwners', new HttpParams()
            .set('fields', 'name,name_laqab,name_kunya,name_ism,name_nasab,name_nisba,comments,sorting,version')
            .set('sort', 'sorting,name_sort,pkid')
        );

        /* Manuscript history patrons http params */
        this.httpParams.set('msHistoryPatrons', new HttpParams()
            .set('fields', 'name,name_laqab,name_kunya,name_ism,name_nasab,name_nisba,comments,sorting,version')
            .set('sort', 'sorting,name_sort,pkid')
        );

        /* Manuscript text transmissions http params */
        this.httpParams.set('msTextTransmissions', new HttpParams()
            .set('fields', 'pkid,fkidnote_types,transliteration,position,translation'
                + ',comments,text,version')
            .set('expand', '__images,__annotations')
            .set('sort', 'sorting,pkid')
        );

        /* Manuscript bindings http params */
        this.httpParams.set('msBindings', new HttpParams()
            .set('fields', 'pkid,description,comments,sorting,version')
            .set('expand', '__images,__annotations')
            .set('sort', 'sorting,pkid')
        );

        /* Manuscript decorations http params */
        this.httpParams.set('msDecorations', new HttpParams()
            .set('fields', 'pkid,description,position,comments,sorting,version')
            .set('expand', '__images,__annotations')
            .set('sort', 'sorting,pkid')
        );

        /* Bibliographic links http params */
        this.httpParams.set('biblioLinks', new HttpParams()
            .set('fields', 'pkid,fkidbibliography,pages,notes,sorting,version')
            .set('expand', '__linkedBiblio')
            // .set('sort', 'sorting,pkid')
        );

        /* Manuscript all images (linked or not) */
        this.httpParams.set('msImageFiles', new HttpParams()
            .set('fields', 'pkid,image,name,quotation_label,notes,fkidimg_folders,fkiduse_rights,sorting,version')
            .set('expand', 'fkiduse_rights')
            .set('sort', 'image')
        );

        /* Observables on manuscript data */
        this.mssListFilters$ = new BehaviorSubject<MssListFilters>(new MssListFilters());
        this.manuscripts$ = new BehaviorSubject<ManuscriptList>([]);
        this.manuscript$ = new BehaviorSubject<Manuscript>(<Manuscript>{});
        this.msImageLinks$ = new BehaviorSubject<MsImageLinkList>([]);
        this.msWritingSupportImageLinks$ = new BehaviorSubject<MsImageLinkList>([]);
        this.msTextBlockImageLinks$ = new BehaviorSubject<MsImageLinkList>([]);
        this.msHandsWritingsImageLinks$ = new BehaviorSubject<MsImageLinkList>([]);
        this.msInkHeadingsImageLinks$ = new BehaviorSubject<MsImageLinkList>([]);
        this.msStateOfPreservationImageLinks$ = new BehaviorSubject<MsImageLinkList>([]);
        this.msWorks$ = new BehaviorSubject<MsWorkList>([]);
        this.msHistoryCopies$ = new BehaviorSubject<MsHistoryCopyList>([]);
        this.msHistoryOwners$ = new BehaviorSubject<NameList>([]);
        this.msHistoryPatrons$ = new BehaviorSubject<NameList>([]);
        this.msTextTransmissions$ = new BehaviorSubject<MsTextTransmissionList>([]);
        this.msBindings$ = new BehaviorSubject<MsBindingList>([]);
        this.msDecorations$ = new BehaviorSubject<MsDecorationList>([]);
        this.msBiblioLinks$ = new BehaviorSubject<MsBiblioLinkList>([]);
        this.msImageLinkSelection$ = new BehaviorSubject<MsImageLinkSelection>(new MsImageLinkSelection());
        this.msImageFileList$ = new BehaviorSubject<MsImageFileList>([]);
    }

    /* Parse filters and update http params */
    private updateFilterParams(mssListFilters: MssListFilters): void {
        const filtersString: string = mssListFilters.stringifyAllFilters();
        this.httpParams.set('msList', this.httpParams.get('msList').set('filter', filtersString));
    }

    /* Manuscripts API call */
    private loadManuscripts(): void {
        this.http.get<ManuscriptList>(
            environment.endpoints.de + '/manuscripts',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msList')
            })
            .subscribe(
                response => this.manuscripts$.next(response),
                error => this.manuscripts$.error(error)
            );
    }

    /* Single manuscript API call */
    private loadManuscript(manId: number): void {
        this.http.get<Manuscript>(
            environment.endpoints.de + '/manuscripts/' + manId,
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('ms')
            })
            .subscribe(
                response => this.manuscript$.next(response),
                error => this.manuscript$.error(error)
            );
    }

    /* Load manuscript image links API call */
    private loadManuscriptImageLinks(manId: number): void {
        this.http.get<MsImageLinkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/image-links/all',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msImageLinks')
            })
            .subscribe(
                response => this.msImageLinks$.next(response),
                error => this.msImageLinks$.error(error)
            );
    }

    /* Load writing support image links API call */
    private loadWritingSupportImageLinks(manId: number): void {
        this.http.get<MsImageLinkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/image-links/writing-support',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('imageLinks')
            })
            .subscribe(
                response => this.msWritingSupportImageLinks$.next(response),
                error => this.msWritingSupportImageLinks$.error(error)
            );
    }

    /* Load text block image links API call */
    private loadTextBlockImageLinks(manId: number): void {
        this.http.get<MsImageLinkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/image-links/text-blocks',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('imageLinks')
            })
            .subscribe(
                response => this.msTextBlockImageLinks$.next(response),
                error => this.msTextBlockImageLinks$.error(error)
            );
    }

    /* Load hands writings image links API call */
    private loadHandsWritingsImageLinks(manId: number): void {
        this.http.get<MsImageLinkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/image-links/hands-writings',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('imageLinks')
            })
            .subscribe(
                response => this.msHandsWritingsImageLinks$.next(response),
                error => this.msHandsWritingsImageLinks$.error(error)
            );
    }

    /* Load ink headings image links API call */
    private loadInkHeadingsImageLinks(manId: number): void {
        this.http.get<MsImageLinkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/image-links/ink-headings',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('imageLinks')
            })
            .subscribe(
                response => this.msInkHeadingsImageLinks$.next(response),
                error => this.msInkHeadingsImageLinks$.error(error)
            );
    }

    /* Load state of preservation image links API call */
    private loadStateOfPreservationImageLinks(manId: number): void {
        this.http.get<MsImageLinkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/image-links/preservation',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('imageLinks')
            })
            .subscribe(
                response => this.msStateOfPreservationImageLinks$.next(response),
                error => this.msStateOfPreservationImageLinks$.error(error)
            );
    }

    /* Load manuscript works API call */
    private loadManuscriptWorks(manId: number): void {
        this.http.get<MsWorkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/works',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msWorks')
            })
            .subscribe(
                response => this.msWorks$.next(response),
                error => this.msWorks$.error(error)
            );
    }

    /* Load manuscript history copies API call */
    private loadManuscriptHistoryCopies(manId: number): void {
        this.http.get<MsHistoryCopyList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/history/copies',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msHistoryCopies')
            })
            .subscribe(
                response => this.msHistoryCopies$.next(response),
                error => this.msHistoryCopies$.error(error)
            );
    }

    /* Load manuscript history owners API call */
    private loadManuscriptHistoryOwners(manId: number): void {
        this.http.get<NameList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/history/owners',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msHistoryOwners')
            })
            .subscribe(
                response => this.msHistoryOwners$.next(response),
                error => this.msHistoryOwners$.error(error)
            );
    }

    /* Load manuscript history copies API call */
    private loadManuscriptHistoryPatrons(manId: number): void {
        this.http.get<NameList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/history/patrons',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msHistoryPatrons')
            })
            .subscribe(
                response => this.msHistoryPatrons$.next(response),
                error => this.msHistoryPatrons$.error(error)
            );
    }

    /* Load manuscript text transmissions API call */
    private loadManuscriptTextTransmissions(manId: number): void {
        this.http.get<MsTextTransmissionList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/text-transmissions',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msTextTransmissions')
            })
            .subscribe(
                response => this.msTextTransmissions$.next(response),
                error => this.msTextTransmissions$.error(error)
            );
    }

    /* Load manuscript bindings API call */
    private loadManuscriptBindings(manId: number): void {
        this.http.get<MsBindingList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/bindings',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msBindings')
            })
            .subscribe(
                response => this.msBindings$.next(response),
                error => this.msBindings$.error(error)
            );
    }

    /* Load manuscript decorations API call */
    private loadManuscriptDecorations(manId: number): void {
        this.http.get<MsDecorationList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/decorations',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msDecorations')
            })
            .subscribe(
                response => this.msDecorations$.next(response),
                error => this.msDecorations$.error(error)
            );
    }

    /* Load manuscript linked bibliography API call */
    private loadManuscriptBiblioLinks(manId: number): void {
        this.http.get<MsBiblioLinkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/biblio-links',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('biblioLinks')
            })
            .subscribe(
            response => this.msBiblioLinks$.next(
                response.sort((bibLnk1, bibLnk2) => {
                    const bibRef1 = bibLnk1.__linkedBiblio.reference_sort;
                    const bibRef2 = bibLnk2.__linkedBiblio.reference_sort;
                    if (bibRef1 > bibRef2) {
                        return 1;
                    }
                    if (bibRef1 < bibRef2) {
                        return -1;
                    }
                    return 0;
                })
            ),
                error => this.msBiblioLinks$.error(error)
            );
    }

    /* Load manuscript images API call */
    private loadManuscriptImageFiles(): void {
        this.getManuscript().subscribe(
            msData => {
                if (msData.fkidimg_folders > 0) {
                    this.http.get<MsImageFileList>(
                        environment.endpoints.res + '/images/folders/' + msData.fkidimg_folders + '/files',
                        {
                            headers: this.httpHeaders,
                            params: this.httpParams.get('msImageFiles')
                        })
                        .subscribe(
                            response => this.msImageFileList$.next(response),
                            error => this.msImageFileList$.error(error)
                        );
                }
            },
            error => this.msImageFileList$.error(error)
        );
    }

    /* Update filters and reload manuscript list by default */
    updateFilters(mssListFilters: MssListFilters, reloadMss: boolean = true): void {
        this.mssListFilters$.next(mssListFilters);
        this.updateFilterParams(mssListFilters);
        if (reloadMss === true) {
            this.loadManuscripts();
        }
    }

    /* Reset filters and reload manuscript list by default */
    resetFilters(reloadMss: boolean = true): void {
        this.updateFilters(new MssListFilters(), reloadMss);
    }

    /* Select manuscript image links (from those currently loaded), if no input the active selection will be canceled */
    selectMsImageLinks(msImageLinkSelection: MsImageLinkSelection = new MsImageLinkSelection()): void {
        this.msImageLinkSelection$.next(msImageLinkSelection);
    }

    /* Select manuscript image link (from those currently loaded) */
    selectMsImageLink(msImageLink: MsImageLink): void {
        const imageLinkPos = this.msImageLinks$.value.findIndex(
            function (imageLink: MsImageLink) {
                return imageLink.image === msImageLink.image;
            });

        if (imageLinkPos !== -1) {
            this.msImageLinkSelection$.next(
                new MsImageLinkSelection(
                    this.imageGetNamePipe.transform(msImageLink.image).toString(),
                    [msImageLink.image],
                    msImageLink.fkidimg_annotations
                )
            );
        }
    }

    /* Get manuscripts list filters */
    getMssListFilters(): Observable<MssListFilters> {
        return this.mssListFilters$.asObservable();
    }

    /* Get manuscript list */
    getManuscripts(reloadMss: boolean = true): Observable<ManuscriptList> {
        if (reloadMss === true) {
            this.loadManuscripts();
        }
        return this.manuscripts$.asObservable();
    }

    /* Get single manuscript main data, if no valid manuscript identifier is provided return last loaded manuscript */
    getManuscript(manId?: number): Observable<Manuscript> {
        if (manId) {
            this.loadManuscript(manId);
            this.loadManuscriptImageLinks(manId);
            this.loadManuscriptWorks(manId);
            this.loadManuscriptHistoryCopies(manId);
            this.loadManuscriptHistoryOwners(manId);
            this.loadManuscriptHistoryPatrons(manId);
            this.loadManuscriptTextTransmissions(manId);
            this.loadManuscriptBindings(manId);
            this.loadManuscriptDecorations(manId);
            this.loadManuscriptBiblioLinks(manId);
            this.loadWritingSupportImageLinks(manId);
            this.loadTextBlockImageLinks(manId);
            this.loadHandsWritingsImageLinks(manId);
            this.loadInkHeadingsImageLinks(manId);
            this.loadStateOfPreservationImageLinks(manId);
            this.selectMsImageLinks();
            this.loadManuscriptImageFiles();
        }
        return this.manuscript$.asObservable();
    }

    /* Get current loaded manuscript image links */
    getManuscriptImageLinks(): Observable<MsImageLinkList> {
        return this.msImageLinks$.asObservable();
    }

    /* Get current loaded manuscript writing support image links */
    getWritingSupportImageLinks(): Observable<MsImageLinkList> {
        return this.msWritingSupportImageLinks$.asObservable();
    }

    /* Get current loaded manuscript text block image links */
    getTextBlockImageLinks(): Observable<MsImageLinkList> {
        return this.msTextBlockImageLinks$.asObservable();
    }

    /* Get current loaded manuscript hands writings image links */
    getHandsWritingsImageLinks(): Observable<MsImageLinkList> {
        return this.msHandsWritingsImageLinks$.asObservable();
    }

    /* Get current loaded manuscript ink headings image links */
    getInkHeadingsImageLinks(): Observable<MsImageLinkList> {
        return this.msInkHeadingsImageLinks$.asObservable();
    }

    /* Get current loaded manuscript state of preservation image links */
    getStateOfPreservationImageLinks(): Observable<MsImageLinkList> {
        return this.msStateOfPreservationImageLinks$.asObservable();
    }

    /* Get current loaded manuscript works */
    getManuscriptWorks(): Observable<MsWorkList> {
        return this.msWorks$.asObservable();
    }

    /* Get current loaded manuscript history copies */
    getManuscriptHistoryCopies(): Observable<MsHistoryCopyList> {
        return this.msHistoryCopies$.asObservable();
    }

    /* Get current loaded manuscript history owners */
    getManuscriptHistoryOwners(): Observable<NameList> {
        return this.msHistoryOwners$.asObservable();
    }

    /* Get current loaded manuscript history patrons */
    getManuscriptHistoryPatrons(): Observable<NameList> {
        return this.msHistoryPatrons$.asObservable();
    }

    /* Get current loaded manuscript text transmissions */
    getManuscriptTextTransmissions(): Observable<MsTextTransmissionList> {
        return this.msTextTransmissions$.asObservable();
    }

    /* Get current loaded manuscript bindings */
    getManuscriptBindings(): Observable<MsBindingList> {
        return this.msBindings$.asObservable();
    }

    /* Get current loaded manuscript decorations */
    getManuscriptDecorations(): Observable<MsDecorationList> {
        return this.msDecorations$.asObservable();
    }

    /* Get current loaded manuscript bibliographic links */
    getManuscriptBiblioLinks(): Observable<MsBiblioLinkList> {
        return this.msBiblioLinks$.asObservable();
    }

    /* Get manuscript image links active selection */
    getManuscriptImageLinkSelection(): Observable<MsImageLinkSelection> {
        return this.msImageLinkSelection$.asObservable();
    }

    /* Get all manuscript images (linked or not) */
    getManuscriptImageFiles(): Observable<MsImageFileList> {
        return this.msImageFileList$.asObservable();
    }

    /* Search (by content) image links that belongs to a manuscript */
    searchManuscriptImageLinks(manId: number, params: SifaSelection): Observable<MsImageLinkList> {
        const msFilters = new ListFilters();
        if (params.section) {
            msFilters.addTermFilter('fkidgridstruct_sections', params.section.pkid);
        }
        if (params.part) {
            msFilters.addTermFilter('fkidgridstruct_parts', params.part.pkid);
        }
        if (params.treatise) {
            msFilters.addTermFilter('fkidgridstruct_treatises', params.treatise.pkid);
        }
        if (params.chapter) {
            msFilters.addTermFilter('fkidgridstruct_chapters', params.chapter.pkid);
        }
        if (params.otherElement) {
            msFilters.addTermFilter('fkidms_structure', params.otherElement.pkid);
        }

        return this.http.get<MsImageLinkList>(
            environment.endpoints.de + '/manuscripts/' + manId + '/image-links/all',
            {
                headers: this.httpHeaders,
                params: this.httpParams.get('msImageLinks').set('filter', msFilters.stringifyAllFilters())
            });
    }
}



// WEBPACK FOOTER //
// ./src/app/services/manuscripts.service.ts